import { useState, useReducer, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    GridActionsCellItem,
    GridColDef,
    GridRowId,
    useGridApiRef,
    GridApi,
    GridRenderCellParams,
} from '@mui/x-data-grid';
import localforage from 'localforage';
import BasePaper from '@/components/ui/BasePaper';
import BaseCard from '@/components/ui/BaseCard';
import BaseTitle from '@/components/ui/BaseTitle';
import { Create, Delete } from '@mui/icons-material';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import BaseButton from '@/components/ui/BaseButton';
import BaseDialog from '@/components/ui/BaseDialog';
import { BaseAccount, AdminAccountPageState, AdminAccountPageAction } from '@/types/pages/admin/account';
import { deleteAccountAPI, findAllUserAPI } from '@/api/admin/rbac';
import { Link, useNavigate } from 'react-router-dom';
import { useAppContext } from '@/provider/contexts';
import { withPermission } from '@/utils/permissionUtils';
import '@/styles/admin.scss';
import CaptchaCanvas from '@/components/form/CaptchaCanvas';
import SearchBar from '@/components/form/SearchBar';
import { useTranslation } from 'react-i18next';
import BaseDataTable from '@/components/ui/BaseDataTable';
import BaseTableColumn from '@/components/ui/BaseTableColumn';

export type apiRefType = React.MutableRefObject<GridApi>;

function reducer(state: AdminAccountPageState, action: AdminAccountPageAction) {
    switch (action.type) {
        case 'SET_TABLE_LOADING':
            return { ...state, tableLoading: action.payload };
        case 'SET_PAGINATION_MODEL':
            return { ...state, paginationModel: action.payload };
        case 'SET_DATA_TABLE':
            return {
                ...state,
                tableLoading: false,
                dataTableList: action.payload,
            };
        case 'SET_DELETE_ID':
            return {
                ...state,
                deleteId: action.payload,
            };
        case 'SET_CAPTCHA_KEY':
            return {
                ...state,
                captchaKey: action.payload,
            };
        case 'SET_CAPTCHA':
            return {
                ...state,
                captcha: action.payload,
            };
        case 'SET_IS_CAPTCHA_ERROR':
            return {
                ...state,
                isCaptchaError: action.payload,
            };
        case 'CLEAR_CAPTCHA':
            return {
                ...state,
                captchaKey: '',
                captcha: '',
                isCaptchaError: false,
            };
        default:
            return state;
    }
}

function AdminAccountPage() {
    const { t } = useTranslation('common');
    const [search, setSearch] = useState('');
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [captchaErrorMessage, setCaptchaErrorMessage] = useState('');
    const { setNotification } = useAppContext();

    const ProtectedLink = withPermission(Link);

    const nav = useNavigate();

    const defaultState: AdminAccountPageState = {
        tableLoading: false,
        dataTableList: [],
        paginationModel: {
            page: 0,
            pageSize: 5,
        },
        captchaKey: '',
        captcha: '',
        isCaptchaError: false,
    };

    const [{ tableLoading, dataTableList, paginationModel, deleteId, captchaKey, captcha, isCaptchaError }, dispatch] =
        useReducer(reducer, defaultState);
    const fetchAbortControllerRef = useRef<AbortController | null>(null);
    const latestFetchIdRef = useRef(0);

    const getRowId = (row: BaseAccount) => {
        return row.user_id;
    };

    const fetchListHandler = async () => {
        fetchAbortControllerRef.current?.abort();
        const fetchId = latestFetchIdRef.current + 1;
        latestFetchIdRef.current = fetchId;
        const abortController = new AbortController();
        fetchAbortControllerRef.current = abortController;
        dispatch({ type: 'SET_TABLE_LOADING', payload: true });
        try {
            const corporationId = await localforage.getItem('corporation_id');
            if (!corporationId) {
                setNotification({ message: t('Corporation not found'), severity: 'error' });
                return;
            }

            const {
                data: { data },
            } = await findAllUserAPI(
                { corporate_id: corporationId as number, keyword: '#ALL#' },
                { signal: abortController.signal },
            );
            if (abortController.signal.aborted || latestFetchIdRef.current !== fetchId) return;
            dispatch({ type: 'SET_DATA_TABLE', payload: data });
        } catch (error) {
            if (abortController.signal.aborted) return;
            setNotification({ message: t('An unexpected error occurred'), severity: 'error' });
        } finally {
            if (latestFetchIdRef.current === fetchId) {
                dispatch({ type: 'SET_TABLE_LOADING', payload: false });
                fetchAbortControllerRef.current = null;
            }
        }
    };

    const renderSimpleCell = useCallback((params: GridRenderCellParams) => {
        const value = params.value;
        const displayValue = value === null || value === undefined || value === '' ? '-' : String(value);
        return <BaseTableColumn columnValue={displayValue} />;
    }, []);

    const editItem = useCallback(
        (id: GridRowId) => {
            nav('/admin/account/' + id);
        },
        [nav],
    );

    const deleteItem = useCallback(
        async (id: GridRowId) => {
            const deleteId = Number(id);
            dispatch({ type: 'SET_DELETE_ID', payload: deleteId });
            try {
                const {
                    data: { data, isSuccess, message },
                } = await deleteAccountAPI(deleteId);

                if (isSuccess) {
                    setCaptchaErrorMessage('');
                    setIsDeleteDialogOpen(true);
                    dispatch({ type: 'SET_CAPTCHA_KEY', payload: data.captcha });
                } else {
                    setNotification({ message: message, severity: 'error' });
                }
            } catch (error) {
                setNotification({ message: error as string, severity: 'error' });
            }
        },
        [setNotification],
    );

    const columns: GridColDef[] = useMemo(
        () => [
            { field: 'username', headerName: t('Account'), flex: 1, minWidth: 140, renderCell: renderSimpleCell },
            { field: 'email', headerName: t('Email'), flex: 1, minWidth: 140, renderCell: renderSimpleCell },
            {
                field: 'action',
                type: 'actions',
                headerName: t('Action'),
                flex: 1,
                minWidth: 90,
                hideable: false,
                disableColumnMenu: false,
                getActions: ({ id }) => {
                    return [
                        <GridActionsCellItem
                            icon={<Create />}
                            label={t('Edit')}
                            sx={{
                                color: 'primary.main',
                            }}
                            onClick={() => editItem(id)}
                        />,
                        <GridActionsCellItem
                            icon={<Delete />}
                            label={t('Delete')}
                            className="textPrimary"
                            onClick={() => deleteItem(id)}
                            color="inherit"
                        />,
                    ];
                },
            },
        ],
        [deleteItem, editItem, renderSimpleCell, t],
    );

    useEffect(() => {
        fetchListHandler();
        return () => {
            fetchAbortControllerRef.current?.abort();
        };
    }, []);

    const apiRef = useGridApiRef();

    const refreshCaptcha = async (errorMessage = '') => {
        try {
            const {
                data: { data, isSuccess, message },
            } = await deleteAccountAPI(deleteId as number);

            if (isSuccess) {
                dispatch({ type: 'CLEAR_CAPTCHA' });
                dispatch({ type: 'SET_CAPTCHA_KEY', payload: data.captcha });
                setCaptchaErrorMessage(errorMessage);
            } else {
                setNotification({ message, severity: 'error' });
            }
        } catch (error) {
            console.error('An unexpected error occurred:', error);
            setNotification({ message: t('An unexpected error occurred'), severity: 'error' });
        }
    };

    const handleConfirmDelete = async () => {
        if (captchaKey !== captcha) {
            setCaptchaErrorMessage('');
            dispatch({ type: 'SET_IS_CAPTCHA_ERROR', payload: true });
            return;
        }

        try {
            const {
                data: { data, isSuccess, message },
            } = await deleteAccountAPI(deleteId as number, { captcha });

            if (isSuccess) {
                setIsDeleteDialogOpen(false);
                setNotification({ message: message, severity: 'success' });
                fetchListHandler();
            } else if (data?.error_code === '0x01000F') {
                await refreshCaptcha(t('The verification code has expired.'));
                return;
            } else {
                setNotification({ message: message, severity: 'error' });
            }
        } catch (error) {
            console.error('An unexpected error occurred:', error);
            setNotification({ message: t('An unexpected error occurred'), severity: 'error' });
        }

        setIsDeleteDialogOpen(false);
    };

    const closeDeleteDialogHandler = () => {
        setIsDeleteDialogOpen(false);
        setCaptchaErrorMessage('');
        dispatch({ type: 'CLEAR_CAPTCHA' });
    };

    return (
        <>
            <BasePaper className={'admin-paper'}>
                <BaseTitle icon={<ManageAccountsIcon />} title={t('Administration of Account')} />

                <BaseCard>
                    <div className="card-header">
                        <SearchBar
                            value={search}
                            placeholder={t('Search')}
                            onSearch={(val) => {
                                setSearch(val);
                                apiRef.current.setQuickFilterValues([val]);
                            }}
                            sx={{ my: 2 }}
                        />
                        <ProtectedLink permission="page-adminAccount-all-write" to="/admin/account/new">
                            <BaseButton startIcon={<Create />}>{t('New Account')}</BaseButton>
                        </ProtectedLink>
                    </div>
                    <BaseDataTable
                        rows={dataTableList}
                        columns={columns}
                        getRowId={getRowId}
                        loading={tableLoading}
                        apiRef={apiRef}
                        paginationModel={paginationModel}
                        onPaginationModelChange={(model) => {
                            dispatch({ type: 'SET_PAGINATION_MODEL', payload: model });
                        }}
                    />

                    <BaseDialog
                        dialogTitle={t('Delete Account')}
                        confirmButtonText={t('Confirm')}
                        cancelButtonText={t('Cancel')}
                        onConfirm={handleConfirmDelete}
                        onClose={closeDeleteDialogHandler}
                        open={isDeleteDialogOpen}
                    >
                        <CaptchaCanvas
                            key={captchaKey}
                            captchaKey={captchaKey}
                            isCaptchaError={isCaptchaError}
                            errorMessage={captchaErrorMessage}
                            onRefreshCaptcha={() => void refreshCaptcha()}
                            setCaptcha={(captcha) => dispatch({ type: 'SET_CAPTCHA', payload: captcha })}
                        />
                    </BaseDialog>
                </BaseCard>
            </BasePaper>
        </>
    );
}

export default AdminAccountPage;
